import React, { useEffect, useState } from "react";
import { Plus, Radio, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState, Badge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function EducatorLive() {
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const load = () => api.get("/live-classes").then(({ data }) => setClasses(data));
  useEffect(() => { load(); api.get("/my/courses").then(({ data }) => setCourses(data)); }, []);

  return (
    <div data-testid="educator-live">
      <PageHeader title="Live Classes" subtitle="Schedule and manage live sessions" action={<ScheduleDialog courses={courses} onDone={load} />} />
      {classes.length === 0 ? <EmptyState icon={Radio} title="No classes scheduled" subtitle="Schedule a live class for your students." /> : (
        <div className="space-y-3">
          {classes.map((l) => (
            <div key={l.id} className="bg-white border border-border rounded-lg p-5 flex items-center justify-between" data-testid={`elive-${l.id}`}>
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-lg bg-crimson/10 text-crimson flex items-center justify-center"><Radio size={20} /></div>
                <div><h3 className="font-semibold text-navy">{l.title}</h3><p className="text-sm text-muted-foreground">{l.date} • {l.start_time} - {l.end_time}</p></div>
              </div>
              <div className="flex items-center gap-3">
                <Badge tone="amber">{l.status}</Badge>
                {l.meeting_url && <a href={l.meeting_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline">Open <ExternalLink size={13} className="ml-1" /></Button></a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScheduleDialog({ courses, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ course_id: "", title: "", subject: "", topic: "", date: "", start_time: "", end_time: "", meeting_url: "", description: "", recording_enabled: true });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.course_id || !f.title || !f.date) return toast.error("Fill required fields");
    await api.post("/live-classes", f); toast.success("Live class scheduled — learners notified"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="viriks-navy text-white" data-testid="schedule-live-btn"><Plus size={16} className="mr-1" /> Schedule Class</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>Schedule Live Class</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Course</Label>
            <Select value={f.course_id} onValueChange={(v) => set("course_id", v)}><SelectTrigger data-testid="live-course"><SelectValue placeholder="Select course" /></SelectTrigger>
              <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="live-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Subject</Label><Input value={f.subject} onChange={(e) => set("subject", e.target.value)} /></div>
            <div><Label>Topic</Label><Input value={f.topic} onChange={(e) => set("topic", e.target.value)} /></div>
            <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="live-date" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Start</Label><Input type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} /></div>
              <div><Label>End</Label><Input type="time" value={f.end_time} onChange={(e) => set("end_time", e.target.value)} /></div>
            </div>
          </div>
          <div><Label>Meeting URL (Zoom / Meet / YouTube)</Label><Input value={f.meeting_url} onChange={(e) => set("meeting_url", e.target.value)} data-testid="live-url" placeholder="https://meet.google.com/..." /></div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.recording_enabled} onChange={(e) => set("recording_enabled", e.target.checked)} /> Enable recording</label>
        </div>
        <DialogFooter><Button className="viriks-navy text-white" onClick={save} data-testid="save-live">Schedule</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
